import { useCallback, useEffect, useRef } from "react"

import { TRACKING_EVENTS } from "./events"
import { useAnalytics, type AnalyticsProperties } from "./useAnalytics"

/**
 * Hook to track component load time performance
 * Measures from component mount until isLoaded becomes true
 */
export const useComponentLoadTime = (
  componentName: string,
  isLoaded = true,
  additionalProperties?: AnalyticsProperties,
) => {
  const { trackEvent } = useAnalytics()
  const startTimeRef = useRef<number | null>(null)
  const hasTrackedRef = useRef<boolean>(false)

  useEffect(() => {
    // Start tracking on mount
    if (startTimeRef.current === null) {
      startTimeRef.current = performance.now()
    }
  }, [])

  useEffect(() => {
    // Track when component is fully loaded
    if (isLoaded && startTimeRef.current !== null && !hasTrackedRef.current) {
      const endTime = performance.now()
      const loadTime = endTime - startTimeRef.current
      hasTrackedRef.current = true

      trackEvent(TRACKING_EVENTS.PERFORMANCE.COMPONENT_LOAD_TIME, {
        component_name: componentName,
        load_time_ms: Math.round(loadTime),
        load_time_seconds: Math.round((loadTime / 1000) * 100) / 100,
        ...additionalProperties,
      })
    }
  }, [isLoaded, componentName, additionalProperties, trackEvent])

  // Return a function to manually trigger tracking if needed
  const handleManualTrack = useCallback(() => {
    if (startTimeRef.current !== null && !hasTrackedRef.current) {
      const endTime = performance.now()
      const loadTime = endTime - startTimeRef.current
      hasTrackedRef.current = true

      trackEvent(TRACKING_EVENTS.PERFORMANCE.COMPONENT_LOAD_TIME, {
        component_name: componentName,
        load_time_ms: Math.round(loadTime),
        load_time_seconds: Math.round((loadTime / 1000) * 100) / 100,
        ...additionalProperties,
      })
    }
  }, [componentName, additionalProperties, trackEvent])

  return { handleManualTrack }
}
